import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchCartItems = useCallback(async () => {
    const token = localStorage.getItem("jwt");

    if (!token) {
      navigate("/login-register");
      return;
    }

    try {
      // Fetch user email
      const userRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!userRes.ok) throw new Error("Failed to fetch user");
      const userData = await userRes.json();

      // Fetch cart items
      const cartRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/carts?filters[email][$eq]=${userData.email}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!cartRes.ok) throw new Error("Failed to fetch cart");
      const cartData = await cartRes.json();

      // Fetch all products
      const [cartProducts] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/tools`),
      ]);

      const cartProductsData = await cartProducts.json();

      // Create a map of all products by slug
      const cartBySlug = new Map();

      cartProductsData.data.forEach((item) => {
        const productData = { id: item.id, ...(item.attributes || item) };
        cartBySlug.set(productData.slug, {
          name: productData.name,
          price: Number(productData.price),
          image: productData.image,
          category: productData.category,
        });
      });

      // Map cart items with product details
      const items = cartData.data
        .map((item) => {
          const cartItemData = { id: item.id, ...(item.attributes || item) };
          return {
            id: cartItemData.id,
            slug: cartItemData.slug,
            ...cartBySlug.get(cartItemData.slug),
          };
        })
        .filter((item) => item.name && item.price);

      setCartItems(items);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  // Rest of the component remains the same, just update API URLs
  const handlePromoSubmit = async () => {
    const token = localStorage.getItem("jwt");
    if (!token || !promoCode) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/promo-codes?filters[code][$eq]=${promoCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (data.data && data.data.length > 0) {
        const promoData = { id: data.data[0].id, ...(data.data[0].attributes || data.data[0]) };
        setPromoDiscount(promoData.discount);
        setPromoError("");
        setIsPromoOpen(false);

        toast.success("Promo code applied!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Invalid promo code!", {
          position: "top-right",
          autoClose: 3000,
        });
        setPromoDiscount(0);
      }
    } catch (error) {
      console.error("Error checking promo code:", error);
      setPromoError("Error checking promo code");
      setPromoDiscount(0);
    }
  };

  // handle checkout
  const handleCheckout = async () => {
    if (!isScriptLoaded) {
      alert("Payment system is still loading. Please try again in a moment.");
      return;
    }

    const token = localStorage.getItem("jwt");
    if (!token) {
      alert("Please log in to proceed with the purchase.");
      navigate("/login-register");
      return;
    }

    try {
      // Fetch user details
      const userRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!userRes.ok) throw new Error("Failed to fetch user details");
      const userData = await userRes.json();

      // Create Razorpay order
      const orderRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/createOrder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: calculateTotal() * 100,
            currency: "INR",
            receipt: `cart-purchase-${Date.now()}`,
            tool: {
              toolName: "Cart Purchase",
              toolImage: cartItems[0]?.image || "",
            },
            userEmail: userData.email,
          }),
        }
      );

      const orderData = await orderRes.json();

      if (!orderData.id) throw new Error("Failed to create order");

      const options = {
        key: "rzp_test_yCsNcxlsD8WqnC",
        amount: orderData.amount,
        currency: orderData.currency,
        name: "PPDDEH",
        description: "Cart Purchase",
        order_id: orderData.id,
        prefill: {
          email: userData.email,
        },
        theme: {
          color: "#3B82F6",
        },
        handler: async function (response) {
          try {
            const verifyRes = await fetch(
              `${process.env.REACT_APP_API_URL}/api/verifyPayment`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(response),
              }
            );

            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              // Clear cart after successful payment
              const deletePromises = cartItems.map((item) =>
                fetch(`${process.env.REACT_APP_API_URL}/api/carts/${item.id}`, {
                  method: "DELETE",
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
              );

              await Promise.all(deletePromises);

              toast.success("Purchase successful!", {
                position: "top-right",
                autoClose: 3000,
              });

              setCartItems([]);
            }
          } catch (error) {
            console.error("Error during payment verification:", error);
            alert("Payment verification failed. Please contact support.");
          }
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error("Error during checkout:", error);
      alert("Checkout failed. Please try again.");
    }
  };

  // Rest of the component remains exactly the same
  const calculateTotal = () => {
    const subtotal = cartItems.reduce((acc, item) => acc + item.price, 0);
    const discount = subtotal * (promoDiscount / 100);
    return Math.round(subtotal - discount);
  };

  const handleRemove = async (itemId) => {
    const token = localStorage.getItem("jwt");
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/carts/${itemId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        toast.error("Failed to remove item from the cart!", {
          position: "top-right",
          autoClose: 3000,
        });
      }

      setCartItems((prevItems) =>
        prevItems.filter((item) => item.id !== itemId)
      );

      toast.success("Item removed from your cart!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error removing item:", error);
      alert("Failed to remove the item. Please try again.");
    }
  };

  // Loading state and return JSX remain exactly the same
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-start p-4">
      <h1 className="text-2xl font-bold mb-6 text-center">Shopping Cart</h1>

      <div className="max-w-3xl w-full">
        {cartItems.length === 0 ? (
          <p className="text-gray-500 text-center mt-5">Your cart is empty.</p>
        ) : (
          <div>
            <div className="border rounded-lg shadow-md mt-5">
              <div className="divide-y">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h2 className="font-semibold text-lg">{item.name}</h2>
                      <p className="text-gray-500">{item.category}</p>
                    </div>
                    <div>
                      <p className="font-bold">Price: ₹{item.price}</p>
                      <button
                        onClick={() => handleRemove(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 flex flex-col bg-gray-50">
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-black mb-2">
                    <span>Discount ({promoDiscount}%):</span>
                    <span>
                      -₹
                      {(
                        cartItems.reduce((acc, item) => acc + item.price, 0) *
                        (promoDiscount / 100)
                      ).toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-xl font-bold">₹{calculateTotal()}</span>
                </div>
              </div>
            </div>

            <button
              className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 mt-4"
              onClick={handleCheckout}
            >
              Checkout
            </button>

            <p className="text-center mt-10">
              Got any promo codes?{" "}
              <span
                className="cursor-pointer text-blue-500 hover:text-blue-700 underline"
                onClick={() => setIsPromoOpen(true)}
              >
                Apply here.
              </span>
            </p>
          </div>
        )}
      </div>

      {isPromoOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-80 shadow-lg">
            <h3 className="text-xl font-semibold mb-4">Enter Promo Code</h3>
            <input
              type="text"
              className="w-full px-4 py-2 border rounded-lg mb-4"
              placeholder="Promo Code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            />
            {promoError && (
              <p className="text-red-500 text-sm mb-4">{promoError}</p>
            )}
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg"
                onClick={() => setIsPromoOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                onClick={handlePromoSubmit}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Cart;
